<template>
  <div class="mission-year-selector" :class="{ 'is-fixed': isFixed }">
    <div class="mission-year-selector__main">
      <button
        class="mission-year-selector__prev"
        :disabled="isDisabledPrev"
        @click="onChangeMissionYear(selectedMissionYear - 1)"
      >
        <mission-left-icon :width="20" :height="20" />
      </button>
      <p class="mission-year-selector__current">{{ selectedMissionYear }}</p>
      <button
        class="mission-year-selector__next"
        :disabled="isDisabledNext"
        @click="onChangeMissionYear(selectedMissionYear + 1)"
      >
        <mission-right-icon :width="20" :height="20" />
      </button>
    </div>
    <button v-if="showButton" class="mission-year-selector__button" @click="handleCheckInClicked">
      <mission-check-in-icon :width="18" :height="18" class="mission-year-selector__button-icon" />
      <span>CHECK IN</span>
    </button>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from '@vue/composition-api'
import dayjs from 'dayjs'
import MissionCheckInIcon from '@/components/MissionPage/MissionListPane/parts/icons/MissionCheckInIcon.vue'
import MissionLeftIcon from '@/components/MissionPage/MissionListPane/parts/icons/MissionLeftIcon.vue'
import MissionRightIcon from '@/components/MissionPage/MissionListPane/parts/icons/MissionRightIcon.vue'

export default defineComponent({
  name: 'MissionYearSelectorParts',
  components: { MissionRightIcon, MissionLeftIcon, MissionCheckInIcon },
  props: {
    /**
     * ミッション一覧画面で表示する年度
     */
    selectedMissionYear: {
      type: Number,
      required: true,
    },
    isFixed: {
      type: Boolean,
    },
    showCheckIn: {
      type: Boolean,
    },
  },
  setup(props, { emit }) {
    const showButton = computed(() => props.isFixed && !props.showCheckIn)
    const handleCheckInClicked = () => {
      emit('onClickCheckIn')
    }

    /**
     * 前年度のミッションを見る活性/非活性判定
     */
    const isDisabledPrev = computed(
      () =>
        // 2024年より前には戻れない
        props.selectedMissionYear < 2025,
    )

    /**
     * 次年度のミッションを見る活性/非活性判定
     */
    const isDisabledNext = computed(() => {
      const thisYear = new Date().getFullYear()
      if (thisYear - 1 > props.selectedMissionYear) {
        // 前々年度以前のミッションを見ている場合は次年度のミッショを見ることができる
        return false
      }
      if (thisYear > props.selectedMissionYear) {
        // 前年度のミッションを見ている場合、2/1 00:00:00(JST)以降であれば次年度のミッションを見ることができる
        return (
          dayjs.tz(`${thisYear}-02-01`, 'Asia/Tokyo').startOf('day').valueOf() > dayjs().valueOf()
        )
      }

      // 同年度のミッションを見ている場合、次年度のミッションを見ることができない
      return true
    })

    /**
     * ミッション一覧画面の年度を変更
     */
    const onChangeMissionYear = (changedYear: number) => {
      emit('onChangeMissionYear', changedYear)
    }

    return {
      showButton,
      handleCheckInClicked,
      isDisabledPrev,
      isDisabledNext,
      onChangeMissionYear,
    }
  },
})
</script>

<style scoped lang="scss">
.mission-year-selector {
  position: relative;
  height: 46px;
  padding: 12px 0;

  &__main {
    display: flex;
    gap: 16px;
    align-items: center;
    justify-content: center;
  }

  &__current {
    font-size: 18px;
    font-weight: 600;
    letter-spacing: 0.08em;
  }

  &__prev,
  &__next {
    display: flex;
    color: #fff;
    transform: translateY(-1px);

    &:disabled {
      pointer-events: none;
      opacity: 0.3;
    }
  }

  &__button {
    position: absolute;
    top: 50%;
    right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 103px;
    height: 30px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: $black20;
    background-color: $white;
    border-radius: 6px;
    transform: translateY(-50%);
  }

  &__button-icon {
    margin-right: 5px;
  }
}
</style>
